import api from './api';
import { 
  normalizeTrilha, 
  normalizeUserAdmin, 
  normalizeCursoAdmin, 
  normalizeTrilhaAdmin, 
  normalizeEmpresaAdmin,
  normalizeGrupoAdmin,
  normalizeGrupoTrilha,
  normalizeGrupoUsuario,
  normalizeLessonAdmin,
} from '../utils/userAdapter';
import type {
  Curso,
  Trilha,
  Aula,
  ProgressoResumo,
  Quiz,
  TentativaQuiz,
  Certificado,
  Turma,
  Documento,
  Favorito,
  Anotacao,
  LibraryDocumentsResponse,
  CreateUserDTO,
  UpdateUserDTO,
  CreateTrilhaDTO,
  UpdateTrilhaDTO,
  CreateCursoDTO,
  UpdateCursoDTO,
  CreateEmpresaDTO,
  UpdateEmpresaDTO,
  CreateGrupoDTO,
  UpdateGrupoDTO,
  AddTrilhaToGrupoDTO,
  Lesson,
  CreateLessonDTO,
  UpdateLessonDTO,
  CreateYouTubeLessonDTO,
  DashboardStats,
  CountResponse,
} from '../types';

// ============================================================================
// HELPERS
// ============================================================================
/**
 * Remove parâmetros vazios, undefined ou null de um objeto
 */
const cleanParams = <T extends Record<string, unknown>>(params?: T): T | undefined => {
  if (!params) return undefined;
  
  const cleaned = Object.entries(params).reduce((acc, [key, value]) => {
    // Remove se for undefined, null, ou string vazia
    if (value !== undefined && value !== null && value !== '') {
      acc[key] = value;
    }
    return acc;
  }, {} as Record<string, unknown>);
  
  // Se não sobrou nenhum parâmetro, retorna undefined
  return Object.keys(cleaned).length > 0 ? (cleaned as T) : undefined;
};

// ============================================================================
// AUTENTICAÇÃO
// ============================================================================
export const authService = {
  login: async (email: string, password: string) => {
    const { data } = await api.post('/Auth/login', { email, password });
    return data;
  },
  
  register: async (userData: CreateUserDTO) => {
    const { data } = await api.post('/Auth/register', userData);
    return data;
  },

  forgotPassword: async (email: string) => {
    const { data } = await api.post('/Auth/forgot-password', { email });
    return data as { message: string };
  },

  resetPassword: async (token: string, newPassword: string) => {
    const { data } = await api.post('/Auth/reset-password', { token, newPassword });
    return data as { message: string };
  },
  
  logout: async () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  },
  
  getCurrentUser: async () => {
    const { data } = await api.get('/Users/me');
    return data;
  },
};

// ============================================================================
// CATÁLOGO
// ============================================================================
export const catalogService = {
  getCursos: async (params?: {
    search?: string;
    categoria?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const cleanedParams = cleanParams(params);
    const { data } = await api.get<Curso[]>('/Cursos', { params: cleanedParams });
    return data;
  },
  
  getTrilhas: async () => {
    const { data } = await api.get<Trilha[]>('/Trilhas');
    return data.map(normalizeTrilha);
  },

  getTrilha: async (id: string) => {
    const { data } = await api.get<Trilha>(`/Trilhas/${id}`);
    return normalizeTrilha(data);
  },
  
  getCursoBySlug: async (slug: string) => {
    const { data } = await api.get<Curso>(`/Cursos/slug/${slug}`);
    return data;
  },
  
  getTrilhaBySlug: async (slug: string) => {
    const { data } = await api.get<Trilha>(`/Trilhas/slug/${slug}`);
    return data;
  },

  getCurso: async (id: string) => {
    const { data } = await api.get<any>(`/Cursos/${id}`);
    
    // Buscar lessons separadamente (endpoint requer autenticação)
    let lessons: any[] = [];
    try {
      const lessonsResponse = await api.get<any[]>(`/Lessons/course/${id}`);
      lessons = lessonsResponse.data || [];
    } catch (error) {
      console.warn('Não foi possível carregar as lessons (pode exigir autenticação):', error);
    }
    
    // Adaptar resposta do backend (inglês) para frontend (português)
    return {
      id: data.id,
      slug: data.slug || data.id, // Se não tiver slug, usa o ID
      titulo: data.title || data.titulo,
      descricao: data.description || data.descricao,
      capa: data.coverImage || data.capa,
      nivel: data.level || data.nivel,
      categoria: data.category || data.categoria,
      cargaHorariaMin: data.durationMinutes || data.cargaHorariaMin || 0,
      instrutorId: data.instructorId || data.instrutorId,
      instrutor: data.instructorName || data.instrutor,
      preco: data.price || data.preco || 0,
      dataPublicacao: data.createdAt || data.dataPublicacao,
      ativo: data.isActive ?? data.ativo ?? true,
      
      // Converter lessons para estrutura de módulo único
      modulos: lessons.length > 0 ? [{
        id: 'default-module',
        cursoId: data.id,
        titulo: 'Aulas',
        ordem: 1,
        aulas: lessons.map((lesson: any) => ({
          id: lesson.id,
          moduloId: 'default-module',
          titulo: lesson.title || lesson.titulo,
          descricao: lesson.description || lesson.descricao,
          ordem: lesson.order || lesson.ordem || 0,
          tipo: 'video' as const,
          duracaoSeg: (lesson.durationMinutes || 0) * 60,
          videoUrl: lesson.videoUrl,
          progresso: lesson.progress ? {
            status: lesson.isCompleted ? 'concluida' as const : 'em-andamento' as const,
            percentual: lesson.progress || 0
          } : undefined
        }))
      }] : [],
    } as Curso;
  },

  getModulo: async (id: string) => {
    const { data } = await api.get(`/Modulos/${id}`);
    return data;
  },
};

// ============================================================================
// AULAS
// ============================================================================
export const aulaService = {
  getAula: async (id: string) => {
    const { data } = await api.get<Aula>(`/Lessons/${id}`);
    return data;
  },
  
  startAula: async (aulaId: string) => {
    const { data } = await api.post(`/Progress/lessons/${aulaId}/start`);
    return data;
  },
  
  updatePosition: async (aulaId: string, posicao: number) => {
    const { data } = await api.post(`/Progress/lessons/${aulaId}/position`, { posicao });
    return data;
  },
  
  completeAula: async (aulaId: string) => {
    const { data } = await api.post(`/Progress/lessons/${aulaId}/complete`);
    return data;
  },
};

// ============================================================================
// PROGRESSO
// ============================================================================
export const progressoService = {
  getResumo: async () => {
    const { data } = await api.get<ProgressoResumo>('/Progress/summary');
    return data;
  },
};

// ============================================================================
// QUIZZES
// ============================================================================
export const quizService = {
  getQuiz: async (id: string) => {
    const { data } = await api.get<Quiz>(`/Quizzes/${id}`);
    return data;
  },
  
  submitQuiz: async (quizId: string, respostas: Record<string, string>) => {
    const { data } = await api.post<TentativaQuiz>(`/Quizzes/${quizId}/submit`, { respostas });
    return data;
  },
};

// ============================================================================
// CERTIFICADOS
// ============================================================================
export const certificadoService = {
  getMeusCertificados: async () => {
    const { data } = await api.get<Certificado[]>('/Certificates');
    return data;
  },
  
  validarCertificado: async (codigo: string) => {
    const { data } = await api.get(`/Certificates/validate/${codigo}`);
    return data;
  },
};

// ============================================================================
// TURMAS
// ============================================================================
export const turmaService = {
  getMinhasTurmas: async () => {
    const { data } = await api.get<Turma[]>('/Cohorts');
    return data;
  },
  
  getTurma: async (id: string) => {
    const { data } = await api.get<Turma>(`/Cohorts/${id}`);
    return data;
  },
};

// ============================================================================
// BIBLIOTECA
// ============================================================================
export const bibliotecaService = {
  getDocumentos: async (params?: {
    search?: string;
    module?: string;
    page?: number;
  }) => {
    const cleanedParams = cleanParams(params);
    const { data } = await api.get<LibraryDocumentsResponse>('/Library/documents', { params: cleanedParams });
    return data;
  },
  
  getDocumento: async (id: string) => {
    const { data } = await api.get<Documento>(`/Library/documents/${id}`);
    return data;
  },
};

// ============================================================================
// FAVORITOS
// ============================================================================
export const favoritoService = {
  getMeusFavoritos: async () => {
    const { data } = await api.get<Favorito[]>('/Favorites');
    return data;
  },
  
  addFavorito: async (tipo: 'curso' | 'aula' | 'documento', refId: string) => {
    const { data } = await api.post('/Favorites', { tipo, refId });
    return data;
  },
  
  removeFavorito: async (tipo: string, refId: string) => {
    await api.delete(`/Favorites/${tipo}/${refId}`);
  },
};

// ============================================================================
// ANOTAÇÕES
// ============================================================================
export const anotacaoService = {
  getMinhasAnotacoes: async (aulaId?: string) => {
    const { data } = await api.get<Anotacao[]>('/Notes', {
      params: aulaId ? { lessonId: aulaId } : undefined,
    });
    return data;
  },
  
  createAnotacao: async (aulaId: string, texto: string, timestampSeg?: number) => {
    const { data } = await api.post<Anotacao>('/Notes', {
      lessonId: aulaId,
      content: texto,
      timestamp: timestampSeg,
    });
    return data;
  },
  
  updateAnotacao: async (id: string, texto: string) => {
    const { data} = await api.put<Anotacao>(`/Notes/${id}`, { content: texto });
    return data;
  },
  
  deleteAnotacao: async (id: string) => {
    await api.delete(`/Notes/${id}`);
  },
};

// ============================================================================
// ADMIN - USERS
// ============================================================================
export const adminUserService = {
  getUsers: async (params?: { page?: number; search?: string; role?: string }) => {
    const { data } = await api.get('/Users', { params });
    return Array.isArray(data) ? data.map(normalizeUserAdmin) : data;
  },
  
  getUser: async (id: string) => {
    const { data } = await api.get(`/Users/${id}`);
    return normalizeUserAdmin(data);
  },
  
  createUser: async (userData: CreateUserDTO) => {
    const mappedData = {
      name: userData.nome || userData.name,
      email: userData.email,
      password: userData.senha || userData.password,
      phoneNumber: userData.phoneNumber,
      roles: userData.roles || [],
      isActive: userData.ativo !== undefined ? userData.ativo : true,
    };
    const { data } = await api.post('/Users', mappedData);
    return data;
  },
  
  updateUser: async (id: string, userData: UpdateUserDTO) => {
    const mappedData: any = {
      name: userData.nome || userData.name,
      email: userData.email,
      roles: [...new Set(userData.roles || [])], // Remover duplicados
      isActive: userData.ativo ?? userData.isActive ?? true,
    };
    
    // Adicionar phoneNumber apenas se não for vazio
    if (userData.phoneNumber) {
      mappedData.phoneNumber = userData.phoneNumber;
    }
    
    // Adicionar senha apenas se fornecida
    if (userData.senha || userData.password) {
      mappedData.password = userData.senha || userData.password;
    }
    
    const { data } = await api.put(`/Users/${id}`, mappedData);
    return normalizeUserAdmin(data);
  },
  
  deleteUser: async (id: string) => {
    await api.delete(`/Users/${id}`);
  },
};

// ============================================================================
// ADMIN - TRILHAS
// ============================================================================
export const adminTrilhaService = {
  getTrilhas: async (params?: { page?: number; search?: string; ativa?: boolean }) => {
    const { data } = await api.get('/Trilhas', { params });
    return Array.isArray(data) ? data.map(normalizeTrilhaAdmin) : data;
  },
  
  getTrilha: async (id: string) => {
    const { data } = await api.get(`/Trilhas/${id}`);
    return normalizeTrilhaAdmin(data);
  },
  
  createTrilha: async (trilhaData: CreateTrilhaDTO) => {
    const { data } = await api.post('/Trilhas', trilhaData);
    return data;
  },
  
  updateTrilha: async (id: string, trilhaData: UpdateTrilhaDTO) => {
    const { data } = await api.put(`/Trilhas/${id}`, trilhaData);
    return data;
  },
  
  deleteTrilha: async (id: string) => {
    await api.delete(`/Trilhas/${id}`);
  },
};

// ============================================================================
// ADMIN - CURSOS
// ============================================================================
export const adminCursoService = {
  getCursos: async (params?: { page?: number; search?: string; ativo?: boolean; categoria?: string }) => {
    const { data } = await api.get('/Cursos', { params });
    return Array.isArray(data) ? data.map(normalizeCursoAdmin) : data;
  },
  
  getCurso: async (id: string) => {
    const { data } = await api.get(`/Cursos/${id}`);
    return normalizeCursoAdmin(data);
  },
  
  createCurso: async (cursoData: CreateCursoDTO) => {
    const { data } = await api.post('/Cursos', cursoData);
    return data;
  },
  
  updateCurso: async (id: string, cursoData: UpdateCursoDTO) => {
    // Limpar campos vazios antes de enviar
    const cleanedData: any = {
      titulo: cursoData.titulo,
      slug: cursoData.slug,
      descricao: cursoData.descricao,
      categoria: cursoData.categoria,
      ativo: cursoData.ativo,
    };
    
    // Adicionar campos opcionais apenas se não vazios
    if (cursoData.capaUrl) cleanedData.capaUrl = cursoData.capaUrl;
    if (cursoData.duracao) cleanedData.duracao = cursoData.duracao;
    if (cursoData.nivel) cleanedData.nivel = cursoData.nivel.toLowerCase();
    if (cursoData.instrutorId) cleanedData.instrutorId = cursoData.instrutorId;
    
    console.log('🔍 UPDATE CURSO PAYLOAD:', JSON.stringify(cleanedData, null, 2));
    const { data } = await api.put(`/Cursos/${id}`, cleanedData);
    return data;
  },
  
  deleteCurso: async (id: string) => {
    await api.delete(`/Cursos/${id}`);
  },
};

// ============================================================================
// ADMIN - EMPRESAS
// ============================================================================
export const adminEmpresaService = {
  getEmpresas: async (params?: { page?: number; search?: string; ativo?: boolean }) => {
    console.log('🔍 [AdminEmpresaService] Buscando empresas com params:', params);
    const { data } = await api.get('/Empresas', { params });
    console.log('📦 [AdminEmpresaService] Dados brutos recebidos da API:', data);
    console.log('📊 [AdminEmpresaService] Tipo de dados:', Array.isArray(data) ? 'Array' : typeof data);
    
    const normalized = Array.isArray(data) ? data.map(normalizeEmpresaAdmin) : data;
    console.log('✨ [AdminEmpresaService] Dados normalizados:', normalized);
    
    return normalized;
  },
  
  getEmpresa: async (id: string) => {
    const { data } = await api.get(`/Empresas/${id}`);
    return normalizeEmpresaAdmin(data);
  },
  
  createEmpresa: async (empresaData: CreateEmpresaDTO) => {
    console.log('➕ [AdminEmpresaService] Criando nova empresa');
    console.log('📦 [AdminEmpresaService] Payload:', JSON.stringify(empresaData, null, 2));
    
    const { data } = await api.post('/Empresas', empresaData);
    
    console.log('✅ [AdminEmpresaService] Resposta do POST:', data);
    return data;
  },
  
  updateEmpresa: async (id: string, empresaData: UpdateEmpresaDTO) => {
    console.log('✏️ [AdminEmpresaService] Atualizando empresa:', { id, empresaData });
    console.log('📤 [AdminEmpresaService] Endpoint:', `PUT /Empresas/${id}`);
    console.log('📦 [AdminEmpresaService] Payload:', JSON.stringify(empresaData, null, 2));
    
    const { data } = await api.put(`/Empresas/${id}`, empresaData);
    
    console.log('✅ [AdminEmpresaService] Resposta do PUT:', data);
    return data;
  },
  
  deleteEmpresa: async (id: string) => {
    await api.delete(`/Empresas/${id}`);
  },
};

// ============================================================================
// ADMIN - GRUPOS DE TRILHAS
// ============================================================================
export const adminGrupoService = {
  // Gerenciar Grupos
  getGrupos: async (empresaId: string) => {
    const { data } = await api.get('/Grupos', { params: { empresaId } });
    return Array.isArray(data) ? data.map(normalizeGrupoAdmin) : data;
  },
  
  getGrupo: async (id: string) => {
    const { data } = await api.get(`/Grupos/${id}`);
    return normalizeGrupoAdmin(data);
  },
  
  createGrupo: async (empresaId: string, grupoData: CreateGrupoDTO) => {
    console.log('📤 [AdminGrupoService] Criando grupo');
    console.log('🔑 [AdminGrupoService] EmpresaId:', empresaId);
    console.log('📦 [AdminGrupoService] Payload:', JSON.stringify(grupoData, null, 2));
    console.log('🌐 [AdminGrupoService] URL:', `/Grupos?empresaId=${empresaId}`);
    
    const { data } = await api.post('/Grupos', grupoData, { params: { empresaId } });
    
    console.log('✅ [AdminGrupoService] Resposta recebida:', data);
    return normalizeGrupoAdmin(data);
  },
  
  updateGrupo: async (id: string, grupoData: UpdateGrupoDTO) => {
    const { data } = await api.put(`/Grupos/${id}`, grupoData);
    return normalizeGrupoAdmin(data);
  },
  
  deleteGrupo: async (id: string) => {
    await api.delete(`/Grupos/${id}`);
  },

  // Gerenciar Trilhas do Grupo
  getTrilhasDoGrupo: async (grupoId: string) => {
    const { data } = await api.get(`/Grupos/${grupoId}/trilhas`);
    return Array.isArray(data) ? data.map(normalizeGrupoTrilha) : data;
  },
  
  addTrilhaAoGrupo: async (grupoId: string, trilhaData: AddTrilhaToGrupoDTO) => {
    const { data } = await api.post(`/Grupos/${grupoId}/trilhas`, trilhaData);
    return data;
  },
  
  removeTrilhaDoGrupo: async (grupoId: string, trilhaId: string) => {
    await api.delete(`/Grupos/${grupoId}/trilhas/${trilhaId}`);
  },

  // Gerenciar Usuários do Grupo
  getUsuariosDoGrupo: async (grupoId: string) => {
    const { data } = await api.get(`/Grupos/${grupoId}/usuarios`);
    return Array.isArray(data) ? data.map(normalizeGrupoUsuario) : data;
  },
  
  addUsuarioAoGrupo: async (grupoId: string, userId: string) => {
    const { data } = await api.post(`/Grupos/${grupoId}/usuarios/${userId}`);
    return data;
  },
  
  removeUsuarioDoGrupo: async (grupoId: string, userId: string) => {
    await api.delete(`/Grupos/${grupoId}/usuarios/${userId}`);
  },
};

// ============================================================================
// ADMIN - LESSONS (Painel Instrutor)
// ============================================================================
export const adminLessonService = {
  // Listar lessons (todas ou filtradas por curso)
  getLessons: async (params?: { courseId?: string; page?: number; pageSize?: number }) => {
    const cleanedParams = cleanParams(params);
    const { data } = await api.get<Lesson[]>('/Lessons', { params: cleanedParams });
    return Array.isArray(data) ? data.map(normalizeLessonAdmin) : data;
  },
  
  // Buscar lesson por ID
  getLesson: async (id: string) => {
    const { data } = await api.get<Lesson>(`/Lessons/${id}`);
    return normalizeLessonAdmin(data);
  },
  
  // Criar nova lesson (Upload)
  createLesson: async (lessonData: CreateLessonDTO) => {
    const { data } = await api.post<Lesson>('/Lessons', lessonData);
    return normalizeLessonAdmin(data);
  },
  
  // Criar nova lesson (YouTube)
  createYouTubeLesson: async (lessonData: CreateYouTubeLessonDTO) => {
    const { data } = await api.post<Lesson>('/Lessons/youtube', lessonData);
    return normalizeLessonAdmin(data);
  },
  
  // Atualizar lesson
  updateLesson: async (id: string, lessonData: UpdateLessonDTO) => {
    const { data } = await api.put<Lesson>(`/Lessons/${id}`, lessonData);
    return normalizeLessonAdmin(data);
  },
  
  // Deletar lesson
  deleteLesson: async (id: string) => {
    await api.delete(`/Lessons/${id}`);
  },
  
  // Buscar lessons de um curso específico
  getLessonsByCourse: async (courseId: string) => {
    const { data } = await api.get<Lesson[]>(`/Lessons/course/${courseId}`);
    return Array.isArray(data) ? data.map(normalizeLessonAdmin) : data;
  },
};

// ============================================================================
// STATS - ESTATÍSTICAS OTIMIZADAS
// ============================================================================
export const statsService = {
  // Endpoint agregado - Dashboard completo (1 chamada)
  getDashboardStats: async (): Promise<DashboardStats> => {
    const { data } = await api.get<DashboardStats>('/Stats/Dashboard');
    
    // Normalizar para camelCase se vier PascalCase
    return {
      totalUsers: data.TotalUsers ?? data.totalUsers ?? 0,
      totalCursos: data.TotalCursos ?? data.totalCursos ?? 0,
      totalTrilhas: data.TotalTrilhas ?? data.totalTrilhas ?? 0,
      totalEmpresas: data.TotalEmpresas ?? data.totalEmpresas ?? 0,
      totalVideos: data.TotalVideos ?? data.totalVideos ?? 0,
      totalGrupos: data.TotalGrupos ?? data.totalGrupos ?? 0,
    };
  },

  // Endpoints individuais (uso granular)
  getUsersCount: async (): Promise<number> => {
    const { data } = await api.get<CountResponse>('/Stats/Users/Count');
    return data.Count ?? data.count ?? 0;
  },

  getCursosCount: async (): Promise<number> => {
    const { data } = await api.get<CountResponse>('/Stats/Cursos/Count');
    return data.Count ?? data.count ?? 0;
  },

  getTrilhasCount: async (): Promise<number> => {
    const { data } = await api.get<CountResponse>('/Stats/Trilhas/Count');
    return data.Count ?? data.count ?? 0;
  },

  getEmpresasCount: async (): Promise<number> => {
    const { data } = await api.get<CountResponse>('/Stats/Empresas/Count');
    return data.Count ?? data.count ?? 0;
  },

  getVideosCount: async (): Promise<number> => {
    const { data } = await api.get<CountResponse>('/Stats/Videos/Count');
    return data.Count ?? data.count ?? 0;
  },

  getGruposCount: async (): Promise<number> => {
    const { data } = await api.get<CountResponse>('/Stats/Grupos/Count');
    return data.Count ?? data.count ?? 0;
  },
};

